import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, User, UserPlus, UserRound, Users, type LucideIcon } from 'lucide-react';

import { hasPermission } from '@/constants/permissions';
import { useAuthStore } from '@/features/auth/store/auth.store';
import type { RollSummary } from '@/types/student';
import { formatMonth } from '@/lib/format';
import { cn } from '@/lib/utils';
import { instituteToday } from '@/features/teacher/utils';
import type { MonthWorkingDays } from '../api/students.api';
import { Skeleton } from '@/components/ui/Skeleton';
import { ROSTER_CATEGORY_OPTIONS } from '@/components/RosterFilters';

type RosterCategory = (typeof ROSTER_CATEGORY_OPTIONS)[number]['value'];

interface Tile {
  key: keyof RollSummary;
  /** The roster category a click narrows to — the same values as the filter row's dropdown. */
  category: RosterCategory;
  label: string;
  icon: LucideIcon;
  badgeStyle: string;
  cardStyle: string;
  titleStyle: string;
  numberStyle: string;
  hintStyle?: string;
  hint?: (summary: RollSummary, academicYear?: string) => string | undefined;
}

function shareOf(part: number, whole: number): string {
  return whole === 0 ? '—' : `${((part / whole) * 100).toFixed(1)}%`;
}

const TILES: Tile[] = [
  {
    key: 'total',
    category: '',
    label: 'Total students',
    icon: Users,
    badgeStyle: 'bg-indigo-600 text-white shadow-sm shadow-indigo-600/30',
    cardStyle: 'bg-indigo-50/90 dark:bg-indigo-950/40 border-indigo-200/80 dark:border-indigo-800/60 shadow-md hover:shadow-lg hover:shadow-indigo-500/10 hover:-translate-y-0.5',
    titleStyle: 'text-indigo-900 dark:text-indigo-200',
    numberStyle: 'text-indigo-950 dark:text-white',
    hintStyle: 'text-indigo-700/80 dark:text-indigo-300/80',
    hint: () => 'On roll',
  },
  {
    key: 'boys',
    category: 'BOYS',
    label: 'Boys',
    icon: User,
    badgeStyle: 'bg-sky-600 text-white shadow-sm shadow-sky-600/30',
    cardStyle: 'bg-sky-50/90 dark:bg-sky-950/40 border-sky-200/80 dark:border-sky-800/60 shadow-md hover:shadow-lg hover:shadow-sky-500/10 hover:-translate-y-0.5',
    titleStyle: 'text-sky-900 dark:text-sky-200',
    numberStyle: 'text-sky-950 dark:text-white',
    hintStyle: 'text-sky-700/80 dark:text-sky-300/80',
    hint: (summary) => shareOf(summary.boys, summary.total),
  },
  {
    key: 'girls',
    category: 'GIRLS',
    label: 'Girls',
    icon: UserRound,
    badgeStyle: 'bg-rose-600 text-white shadow-sm shadow-rose-600/30',
    cardStyle: 'bg-rose-50/90 dark:bg-rose-950/40 border-rose-200/80 dark:border-rose-800/60 shadow-md hover:shadow-lg hover:shadow-rose-500/10 hover:-translate-y-0.5',
    titleStyle: 'text-rose-900 dark:text-rose-200',
    numberStyle: 'text-rose-950 dark:text-white',
    hintStyle: 'text-rose-700/80 dark:text-rose-300/80',
    hint: (summary) => shareOf(summary.girls, summary.total),
  },
  {
    key: 'newAdmissions',
    category: 'NEW_ADMISSIONS',
    label: 'New admissions',
    icon: UserPlus,
    badgeStyle: 'bg-emerald-600 text-white shadow-sm shadow-emerald-600/30',
    cardStyle: 'bg-emerald-50/90 dark:bg-emerald-950/40 border-emerald-200/80 dark:border-emerald-800/60 shadow-md hover:shadow-lg hover:shadow-emerald-500/10 hover:-translate-y-0.5',
    titleStyle: 'text-emerald-900 dark:text-emerald-200',
    numberStyle: 'text-emerald-950 dark:text-white',
    hintStyle: 'text-emerald-700/80 dark:text-emerald-300/80',
    hint: (_summary, academicYear) => academicYear || undefined,
  },
  // {
  //   key: 'graduated',
  //   label: 'Graduated',
  //   icon: GraduationCap,
  //   badgeStyle: 'bg-purple-600 text-white shadow-sm shadow-purple-600/30',
  //   cardStyle: 'bg-purple-50/90 dark:bg-purple-950/40 border-purple-200/80 dark:border-purple-800/60 shadow-md hover:shadow-lg hover:shadow-purple-500/10 hover:-translate-y-0.5',
  //   titleStyle: 'text-purple-900 dark:text-purple-200',
  //   numberStyle: 'text-purple-950 dark:text-white',
  //   hintStyle: 'text-purple-700/80 dark:text-purple-300/80',
  //   hint: () => 'This session',
  // },
];

export interface StudentsStatRowProps {
  summary: RollSummary | null;
  isLoading: boolean;
  /** The category the list is narrowed to now, so its tile reads as pressed. */
  activeCategory: string;
  onSelect: (category: string) => void;
  /** Days the school was open in the attendance column's month. Null outside the session. */
  attendanceMonth: MonthWorkingDays | null;
  academicYear?: string;
}

export function StudentsStatRow({
  summary,
  isLoading,
  activeCategory,
  onSelect,
  attendanceMonth,
  academicYear,
}: StudentsStatRowProps) {
  // Only the month still running is counted short of its end — say so.
  const isRunningMonth = attendanceMonth?.month === instituteToday().slice(0, 7);

  const figures: RollSummary = summary ?? {
    total: 0,
    boys: 0,
    girls: 0,
    newAdmissions: 0,
    graduated: 0,
  };

  return (
    <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-5">
      {TILES.map(({ key, category, label, icon: Icon, badgeStyle, cardStyle, titleStyle, numberStyle, hintStyle, hint }, index) => {
        const isActive = activeCategory === category;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(category)}
            aria-pressed={isActive}
            className={cn(
              'group flex flex-col justify-between rounded-2xl border p-3  text-left transition-all duration-200 overflow-hidden cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
              cardStyle,
              isActive && '!border-accent ring-1 ring-inset ring-accent/30',
              index === 4 && 'col-span-2 sm:col-span-1',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <span className={cn('text-xs sm:text-[14px] font-bold leading-tight', titleStyle)}>
                {label}
              </span>
              <div className={cn('grid h-8 w-8 sm:h-10 sm:w-10 place-items-center rounded-xl transition-transform duration-200 group-hover:scale-105 shrink-0', badgeStyle)}>
                <Icon className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
              </div>
            </div>

            <div>
              {isLoading ? (
                <Skeleton className="h-7 w-14 mt-1 rounded-lg" />
              ) : (
                <div className="flex items-baseline gap-1.5">
                  <span className={cn('text-2xl font-black tracking-tight tabular-nums truncate', numberStyle)}>
                    {figures[key].toLocaleString('en-IN')}
                  </span>
                  {hint && hint(figures, academicYear) && (
                    <span className={cn('text-[11px] sm:text-xs font-bold', hintStyle)}>
                      ({hint(figures, academicYear)})
                    </span>
                  )}
                </div>
              )}
            </div>
          </button>
        );
      })}

      {/* A figure, not a filter: every child had these same days, so there is
          nothing to narrow the list to. It opens the academic calendar on its
          month instead — the holidays declared there are what the count is
          made of, and where a missing one is put right. */}
      <WorkingDaysFrame month={attendanceMonth?.month ?? null}>
        <div className="flex items-start justify-between gap-2">
          <span className="text-xs font-bold leading-tight text-amber-900 dark:text-amber-200 sm:text-[14px]">
            Working days
          </span>
          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-amber-600 text-white shadow-sm shadow-amber-600/30 sm:h-10 sm:w-10">
            <CalendarDays className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
          </div>
        </div>

        <div>
          {isLoading ? (
            <Skeleton className="mt-1 h-7 w-14 rounded-lg" />
          ) : (
            <div className="flex items-baseline gap-1.5">
              <span className="truncate text-2xl font-black tabular-nums tracking-tight text-amber-950 dark:text-white">
                {attendanceMonth ? attendanceMonth.workingDays : '—'}
              </span>
              <span className="text-[11px] font-bold text-amber-700/80 dark:text-amber-300/80 sm:text-xs">
                (
                {attendanceMonth
                  ? `${formatMonth(`${attendanceMonth.month}-01`)}${isRunningMonth ? ', to today' : ''}`
                  : 'Outside this session'}
                )
              </span>
            </div>
          )}
        </div>
      </WorkingDaysFrame>
    </div>
  );
}

const WORKING_DAYS_FRAME =
  'col-span-2 flex flex-col justify-between overflow-hidden rounded-2xl border border-amber-200/80 bg-amber-50/90 p-3 shadow-md dark:border-amber-800/60 dark:bg-amber-950/40 sm:col-span-1';

/**
 * A link to the calendar while there is a month to show and the reader may
 * open the calendar; a plain box otherwise, rather than a link to a refusal.
 */
function WorkingDaysFrame({ month, children }: { month: string | null; children: ReactNode }) {
  const permissions = useAuthStore((state) => state.user?.permissions);
  const canOpenCalendar = hasPermission(permissions, 'CALENDAR_VIEW');

  if (!month || !canOpenCalendar) return <div className={WORKING_DAYS_FRAME}>{children}</div>;

  return (
    <Link
      to={`/admin/calendar?month=${month}`}
      aria-label={`Working days in ${formatMonth(`${month}-01`)} — open the academic calendar`}
      className={cn(
        WORKING_DAYS_FRAME,
        'group cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
      )}
    >
      {children}
    </Link>
  );
}
